import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraEmptyState, SpecraInlineBanner, SpecraLink } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Home, SearchX, ShieldOff } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { PURSUIT_SECTION_LABELS } from '../components/RecordShell/pursuitNav';
import { useRecordShellEnabled } from '../components/RecordShell/recordShellFlag';
import { useRecordBreadcrumbs } from '../components/RecordShell/useRecordBreadcrumbs';
import { useWorkflowAreas } from '../lib/access/useWorkflowAreas';
import { renderRouterLink } from '../lib/renderRouterLink';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { PursuitRecordContext } from '../pages/Capture/record/PursuitRecordContext';
import { usePursuitHeader } from '../pages/Capture/record/pursuitHooks';
import { NO_CAPABILITIES } from '../pages/Capture/record/pursuitReadTypes';
import { pursuitRedirect } from '../pages/Capture/record/pursuitSections';
/**
 * `/capture/:id` record layout (FND-18, sections GRW-15).
 *
 * Shell on (`growth_record_shell_v1`): legacy `?tab=` / `overviewKind` links
 * redirect to their section, the header read model is loaded once and shared
 * with every section through PursuitRecordContext, the topbar trail is set and
 * the layout's generic body header is hidden. Not-found, denied and merged
 * pursuits get their own states.
 * Shell off: today's page at the index; a nested section URL redirects to the
 * matching `?tab=` so links minted by new code still work.
 */ export function PursuitRecordLayout() {
    const { id = '' } = useParams();
    const location = useLocation();
    const enabled = useRecordShellEnabled();
    const rest = location.pathname.slice(`/capture/${id}`.length).replace(/^\/+|\/+$/g, '');
    const section = rest.split('/')[0] ?? '';
    const redirect = pursuitRedirect({
        enabled,
        id,
        rest,
        search: location.search
    });
    const { data: header, isLoading, error } = usePursuitHeader(enabled ? id : '');
    const name = header?.name;
    useRecordBreadcrumbs(enabled && name ? [
        {
            label: 'Home',
            href: '/',
            icon: /*#__PURE__*/ _jsx(Home, {
                className: "h-3 w-3"
            })
        },
        {
            label: 'Growth',
            href: '/growth'
        },
        {
            label: 'Pursuits',
            href: '/capture'
        },
        {
            label: name,
            href: `/capture/${id}`
        },
        // customer/themes keeps "Customer & touchpoints" (its first segment).
        {
            label: PURSUIT_SECTION_LABELS[section] ?? 'Home'
        }
    ] : null);
    // The record shell owns the page title; hide the layout's generic body header while on.
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        if (!enabled) return;
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        enabled,
        setShowHeader
    ]);
    // One audit correlation per record mount, shared by every section's writes.
    const correlationId = useMemo(()=>crypto.randomUUID(), []);
    const value = useMemo(()=>({
            captureProjectId: id,
            zouProjectId: header?.zouProjectId ?? '',
            header,
            capabilities: header?.capabilities ?? NO_CAPABILITIES,
            correlationId
        }), [
        id,
        header,
        correlationId
    ]);
    if (redirect) return /*#__PURE__*/ _jsx(Navigate, {
        replace: true,
        to: redirect
    });
    if (!enabled) return /*#__PURE__*/ _jsx(Outlet, {});
    if (isLoading) {
        return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            className: "m-[var(--space-6)]",
            "data-testid": Testids.deliveryShared.loadingSkeleton,
            title: "Loading pursuit…"
        });
    }
    if (!header) return /*#__PURE__*/ _jsx(PursuitUnavailable, {
        error: error
    });
    const mergedInto = header.related?.mergedInto;
    return /*#__PURE__*/ _jsxs(PursuitRecordContext.Provider, {
        value: value,
        children: [
            mergedInto && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                "data-testid": Testids.pursuitRecord.mergedBanner,
                tone: "info",
                children: [
                    "Merged into",
                    ' ',
                    mergedInto.href ? /*#__PURE__*/ _jsx(SpecraLink, {
                        href: mergedInto.href,
                        renderLink: renderRouterLink,
                        children: mergedInto.label
                    }) : mergedInto.label,
                    ". This page is kept for history."
                ]
            }),
            /*#__PURE__*/ _jsx(Outlet, {})
        ]
    });
}
function PursuitUnavailable({ error }) {
    const { isLoading: areasLoading, reachable } = useWorkflowAreas();
    const denied = error instanceof ConnectError && error.code === Code.PermissionDenied;
    if (denied) {
        return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            className: "m-[var(--space-6)]",
            "data-testid": Testids.pursuitRecord.denied,
            description: "Ask the pursuit's owner to share it with you.",
            icon: /*#__PURE__*/ _jsx(ShieldOff, {
                className: "h-5 w-5"
            }),
            title: "You don't have access to this pursuit"
        });
    }
    // Not found, or any other failure: the same human not-found as today, never the raw error chain.
    return /*#__PURE__*/ _jsx(SpecraEmptyState, {
        action: !areasLoading && reachable.capture ? /*#__PURE__*/ _jsx(SpecraLink, {
            href: "/capture",
            renderLink: renderRouterLink,
            children: "All pursuits"
        }) : undefined,
        className: "m-[var(--space-6)]",
        "data-testid": Testids.pursuitRecord.notFound,
        description: "It may have been deleted, or you may not have access to it.",
        icon: /*#__PURE__*/ _jsx(SearchX, {
            className: "h-5 w-5"
        }),
        title: "Pursuit not found"
    });
}
